import { TextareaAutosize, InputLabel, FormControl, Typography, Stack } from '@mui/material';
import PropTypes from 'prop-types';
import { RedTheme } from 'common/global';
import { asterikStyles } from 'TAP/styles/genericStyles/asterikStyle';
import { NUMBER_ARRAY } from 'common/global/constants';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { ToolTipWrapper } from 'common/components';

export const GenericCustomTextArea = ({
    headerText,
    value,
    handleTextChange,
    placeHolder,
    borderStyles,
    RequiredField,
    isDisable = false,
    rows = 3,
    maxRows = 5,
    marginTop = '8px',
    inputNameError = false,
    inputErrorMessage,
    characterLimit = 250,
    enableTooltip = false,
    tooltipTittle = ''
}) => {
    const isCharacterLimitExceeded = value?.length > characterLimit;
    const redtheme = RedTheme;

    return (
        <FormControl
            variant="standard"
            data-testid={`${headerText.toLowerCase().replace(/ /g, '_')}_filter`}
            sx={{
                width: '95%',
                mt: 2,
                opacity: isDisable ? 0.6 : 1,
                pointerEvents: isDisable ? 'none' : 'auto',
                fontFamily: '"JohnsonText-Regular"',
                position: 'relative',
            }}
        >
            <Stack direction={'row'} alignItems="center">
                <InputLabel
                    variant="standard"
                    id={`select-${headerText.toLowerCase().replace(/ /g, '-')}-label`}
                    sx={{
                        fontSize: screen?.isTablet ? '20px' : '14px',
                        lineHeight: '22px',
                        fontWeight: 500,
                        fontFamily: '"JohnsonText-Regular"',
                        width: '155px',
                        height: screen?.isTablet ? '25px' : '16px',
                        marginTop: screen?.isTablet ? '5px' : NUMBER_ARRAY.zero,
                        color: redtheme.palette.text.primary,
                    }}
                    shrink
                >
                    {headerText}
                    {enableTooltip && RequiredField && <span style={asterikStyles}>*</span>}
                </InputLabel>
                {enableTooltip && <Stack display={'inline-flex'} marginLeft={'120px'} marginTop={'-2px'}>
                    <ToolTipWrapper
                        title={tooltipTittle}
                        placement='top-start'
                        isOnlyText={true}
                        isCustomHtmlTooltip={true}
                        tooltipCss={{
                            padding: '4px 8px',
                        }}
                    >
                        <HelpOutlineIcon
                            fontSize='medium'
                            sx={{
                                color: `${RedTheme.palette.common.primaryMainBlue}`,
                                height: '18px !important',
                                width: '18px !important',
                            }}
                        />
                    </ToolTipWrapper>
                </Stack>}
                <Typography
                    sx={{
                        position: 'absolute',
                        top: '-2px',
                        right: '0',
                        fontSize: '12px',
                        color: isCharacterLimitExceeded ? `${RedTheme.palette.primary.main}` : `${RedTheme.palette.common.grey}`,
                    }}
                    data-testid={`character-count-${headerText.toLowerCase().replace(/ /g, '_')}`}
                >
                    {value?.length || NUMBER_ARRAY.zero}/{`${characterLimit} Characters`}
                </Typography>
            </Stack>
            <TextareaAutosize
                disabled={isDisable}
                value={value}
                onChange={(e) => handleTextChange(e.target.value)}
                placeholder={placeHolder || 'Enter text'}
                minRows={rows}
                maxRows={maxRows}
                style={{
                    height: '30px',
                    fontFamily: '"JohnsonText-Regular"',
                    fontSize: '16px',
                    fontWeight: 500,
                    marginTop: marginTop,
                    ...(borderStyles
                        ? {
                            border: borderStyles.border,
                            borderRadius: borderStyles.borderRadius,
                            padding: '8px',
                        }
                        : {
                            border: `1px solid ${redtheme.palette.fontColor.gray_orange}`,
                            borderRadius: '4px',
                            padding: '8px',
                        }),
                }}
                data-testid={`${headerText.replace(/ /g, '_')}_textarea`}
            />
            {(inputNameError || isCharacterLimitExceeded) && (
                <Typography
                    sx={{
                        fontSize: '12px',
                        color: `${RedTheme.palette.primary.main}`,
                    }}
                >
                    {inputNameError
                        ? `Character limit exceeded. Max allowed is ${characterLimit} characters.`
                        : inputErrorMessage}
                </Typography>
            )}
        </FormControl>
    );
};

GenericCustomTextArea.propTypes = {
    headerText: PropTypes.string.isRequired,
    value: PropTypes.string,
    handleTextChange: PropTypes.func.isRequired,
    placeHolder: PropTypes.string,
    borderStyles: PropTypes.object,
    RequiredField: PropTypes.bool,
    isDisable: PropTypes.bool,
    rows: PropTypes.number,
    maxRows: PropTypes.number,
    marginTop: PropTypes.string,
    inputNameError: PropTypes.bool,
    inputErrorMessage: PropTypes.string,
    characterLimit: PropTypes.number,
};
